"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";

/**
 * Complete 이용권 업셀 배너 (요약 PDF 상품 상세, JCG-2121/JCG-2060).
 *
 * 요약 PDF 5종 상세 클라이언트(26-1-compressed / 26-1-coding-compressed /
 * 25-3-compressed / 25-3-super-compressed / 25-2-super-compressed)가 공통으로 쓰는
 * "Complete 이용권 안내" 배너. 이전에는 각 클라이언트가 풀폭/컴팩트 두 버전을 복붙해
 * 5곳에 중복돼 있었으나(drift 위험), 한 컴포넌트로 추출해 단일 소스로 통일했다.
 *
 * ## variant
 * - `compact`: 데스크탑 셸 좌측 사이드바(~320–560px 좁은 컬럼). 작은 글씨 + 불릿 + `break-words`.
 * - `full`:    모바일/태블릿 폴백 본문(풀폭). 같은 헤드라인·불릿을 약간 큰 패딩/글씨로.
 *
 * ## 강조 설계 (JCG-2060)
 * "Complete 이용권으로 저렴하게 살 수 있다"를 단일 문장 대신 가치 불릿으로 구조화한다.
 * 불릿 근거는 모두 실제 데이터(`src/types/pass.ts`):
 * - Complete = 요약PDF(이론) + 요약PDF(코딩) 포함
 *   (`PASS_FEATURES.theoryPdf/codingPdf`, `COMPLETE_PASS_FREE_PDFS`)
 * - 프리미엄 기능(오답 노트·문제 풀이 이력·난이도 조절 등) 전부 포함
 * - Complete 정가 30,000원 / 유튜브 구독 25,000원(`PASSES.complete.price/youtubePrice`)
 *   → 이론·코딩 PDF를 따로 사는 것보다 이득(정성적 강조 + 실제 금액).
 *
 * ## 가시성 재설계 → 톤다운 (JCG-2060, 2차)
 * 1차(가시성): amber/orange 그라데이션 위 작은 흰/크림 글씨(`text-amber-50`)는 WCAG 대비가
 *   부족(흰색 on amber-500 ≈ 1.9:1, 작은 텍스트 AA 4.5:1 미달)해 → 진한 솔리드 오렌지 헤더
 *   밴드 + 흰 큰 글씨 + 흰/크림 본문 카드(진한 글씨)로 가시성을 회복했다.
 * 2차(톤다운, 현재): 그 결과 이 배너만 **진한 솔리드 오렌지**라 좌측 사이드바에 함께 쌓이는
 *   형제 배너(코딩 크로스셀=연한 blue 틴트, 적중 실적=연한 purple 틴트)와 채도·명도가 튀어
 *   "오렌지가 너무 쎄다"는 부조화가 생겼다. → **형제와 같은 "연한 틴트 카드" 패밀리로 통일**하되
 *   브랜드 amber(warm) 계열로 정렬했다. 솔리드 오렌지 풀밴드/흰 글씨는 **제거**.
 *   - 단일 카드(헤더 밴드 분리 없음): `bg-amber-50 dark:bg-amber-950/40` 표면 +
 *     `border-amber-300 dark:border-amber-800` 보더. Complete 는 메인 업셀이라 형제의
 *     `border-{c}-200` 보다 **한 단계 진한 `-300`** 으로 살짝만 더 강조(blue/purple 200 ↔ amber 300).
 *   - 글씨는 **연한 배경 위 진한 글씨**로 가시성 유지(1차에서 확보한 대비를 깨지 않음 — 절대
 *     흰색 작은 글씨 on 오렌지로 되돌아가지 않는다): 헤딩 `text-amber-900 dark:text-amber-200`,
 *     불릿 `text-gray-800 dark:text-gray-200`, 강조 `text-amber-800 dark:text-amber-300`.
 *   - 체크마크 `text-amber-600 dark:text-amber-400`(§2 페어), CTA 는 부드러운 amber pill
 *     (`bg-amber-600 ... text-white` / dark `bg-amber-500 text-amber-950`, §2 solid amber 표준).
 *
 * ## 형제 3배너 조화 (좌측 사이드바 트리오)
 * 코딩 크로스셀(blue-50)·적중 실적(purple-50)·Complete(amber-50)가 모두 `-50` 연한 틴트 +
 * `dark:bg-*-950/40` 다크 페어로 채도·명도·시각 무게가 한 세트. Complete 만 보더를 `-300` 으로
 * 두고 CTA pill 을 더해 메인 업셀임을 신호하되 "쎄지" 않게 유지한다.
 *
 * ## 다크모드 (dark-mode-colors.md §2, §4-3)
 * - 더 이상 cross-mode 그라데이션 예외 없음. 모든 색은 light/dark 페어로 명시.
 * - `bg-amber-50 dark:bg-amber-950/40`(형제 카드와 동일 패턴), 텍스트/보더/체크 페어는 §2 표 준수.
 *
 * ## a11y (three-column-layout §isPdfDetail)
 * 헤딩은 `<h2>`(본문 "상품 설명" h2 와 동일 위계). 장식 화살표/체크마크는 `aria-hidden`.
 * Link 는 `focus-visible:ring-2 focus-visible:ring-amber-500`. 좁은 컬럼 대비 `break-words`.
 */

type CompleteUpsellBannerVariant = "compact" | "full";

/** Complete 이용권 가치 불릿 (실제 포함 항목 — src/types/pass.ts SSOT 기반). */
const COMPLETE_PERKS: readonly string[] = [
  "실기 이론 요약 PDF 포함",
  "실기 코딩 요약 PDF 포함",
  "프리미엄 기능(오답 노트·풀이 이력 등) 전부",
  "PDF 따로 사는 것보다 이득",
] as const;

interface CompleteUpsellBannerProps {
  variant: CompleteUpsellBannerVariant;
  /** 추가 className(레이아웃 보정용, 선택). */
  className?: string;
}

export default function CompleteUpsellBanner({
  variant,
  className,
}: CompleteUpsellBannerProps) {
  const isCompact = variant === "compact";

  return (
    <Link
      href="/membership"
      aria-label="Complete 이용권이면 실기 이론·코딩 요약 PDF와 프리미엄 기능이 30,000원에 전부 포함됩니다. 이용권 보기"
      className={cn(
        // 연한 amber 틴트 카드 — 형제 배너(blue-50 코딩 크로스셀·purple-50 적중 실적)와
        // 같은 "연한 틴트 카드" 패밀리. Complete 는 메인 업셀이라 보더만 한 단계 진한 -300
        // (형제 -200 대비) 으로 살짝 강조. 모든 색 light/dark 페어 명시(cross-mode 예외 없음).
        "group block break-words border bg-amber-50 transition-colors",
        "border-amber-300 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:hover:bg-amber-950/60",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2",
        isCompact ? "rounded-xl p-4" : "rounded-2xl p-6",
        className,
      )}
    >
      {/* 헤더 — 연한 배경 위 진한 amber 글씨(가시성 유지), 장식 화살표. */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className={cn(
              "font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400",
              isCompact ? "text-[11px]" : "text-xs",
            )}
          >
            이 PDF, 따로 사지 마세요
          </p>
          <h2
            className={cn(
              "mt-0.5 font-extrabold leading-tight text-amber-900 dark:text-amber-200",
              isCompact ? "text-lg" : "text-2xl",
            )}
          >
            Complete 이용권이면 다 포함
          </h2>
        </div>
        <span
          aria-hidden="true"
          className={cn(
            "shrink-0 leading-none text-amber-600 transition-transform group-hover:translate-x-1 dark:text-amber-400",
            isCompact ? "text-xl" : "text-2xl",
          )}
        >
          &rarr;
        </span>
      </div>

      {/* 가치 불릿 — 진한 글씨(연한 배경 위 고대비) + amber 체크마크. */}
      <ul
        className={cn(
          "mt-3 space-y-1.5 text-gray-800 dark:text-gray-200",
          isCompact ? "text-xs" : "text-sm",
        )}
      >
        {COMPLETE_PERKS.map((perk) => (
          <li key={perk} className="flex items-start gap-2">
            <span
              aria-hidden="true"
              className="shrink-0 font-bold leading-snug text-amber-600 dark:text-amber-400"
            >
              &#10003;
            </span>
            <span className="leading-snug">{perk}</span>
          </li>
        ))}
      </ul>

      {/* 가격 강조 (실제 데이터) — 진한 amber 글씨로 연한 표면 위 고대비. */}
      <p
        className={cn(
          "mt-3 font-bold text-amber-800 dark:text-amber-300",
          isCompact ? "text-xs" : "text-sm",
        )}
      >
        프리미엄 기능 + 이론·코딩 PDF{" "}
        <span className="whitespace-nowrap">30,000원</span>에 전부
      </p>

      {/* CTA 칩 — 부드러운 amber pill(§2 solid amber 표준), 화살표 hover 이동. */}
      <span
        className={cn(
          "mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-600 font-bold text-white transition-colors group-hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:group-hover:bg-amber-400",
          isCompact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm",
        )}
      >
        이용권 보기
        <span
          aria-hidden="true"
          className="transition-transform group-hover:translate-x-0.5"
        >
          &rarr;
        </span>
      </span>
    </Link>
  );
}
